import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  Menu,
  MenuItem,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { reportBackendError } from "@shared/api/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  createSystem,
  deleteSystem,
  exportAssetMappingBySystem,
  exportRequiredEvidencePackBySystem,
  exportTotalEvidencePackBySystem,
  getComponentHierarchy,
  getSystems,
  updateSystem,
} from "../api";
import { ConditionalMenuItem } from "./ConditionalMenuItem";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";
import { LockStatusIcon } from "./LockStatusIcon";
import { StatusChipFromSummary } from "./StatusChip";

interface Props {
  powerPlantId: string;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  isAdmin: boolean;
}

export default function SystemList({
  powerPlantId,
  selectedId,
  onSelect,
  isAdmin,
}: Props) {
  const queryClient = useQueryClient();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | {
    id: string;
    el: HTMLElement;
  }>(null);

  // Unified create/edit dialog
  const [dialog, setDialog] = useState<{
    open: boolean;
    mode: "create" | "edit";
    id: string | null;
    name: string;
    systemType: string;
    prefixes: string[];
    prefixesInput: string;
  }>({
    open: false,
    mode: "create",
    id: null,
    name: "",
    systemType: "",
    prefixes: [],
    prefixesInput: "",
  });

  const openCreate = () =>
    setDialog({
      open: true,
      mode: "create",
      id: null,
      name: "",
      systemType: "",
      prefixes: [],
      prefixesInput: "",
    });

  const openEdit = (sys: {
    id: string;
    name: string;
    system_type: string;
    kks_prefixes: string[];
  }) =>
    setDialog({
      open: true,
      mode: "edit",
      id: sys.id,
      name: sys.name,
      systemType: sys.system_type,
      prefixes: sys.kks_prefixes,
      prefixesInput: "",
    });

  const closeDialog = () => setDialog((d) => ({ ...d, open: false }));

  const {
    data: systems = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["systems", powerPlantId],
    queryFn: () => getSystems(powerPlantId),
    refetchInterval: 30_000,
  });

  const { data: componentHierarchy } = useQuery({
    queryKey: ["componentHierarchy"],
    queryFn: getComponentHierarchy,
    staleTime: Infinity,
  });

  const existingSystemNames = systems.map((s) => s.name.toLowerCase());
  const systemTypeOptions = componentHierarchy
    ? Object.keys(componentHierarchy)
    : [];

  const ownNameLower =
    dialog.mode === "edit"
      ? systems.find((s) => s.id === dialog.id)?.name.toLowerCase()
      : undefined;
  const isDuplicateName =
    !!dialog.name.trim() &&
    existingSystemNames.includes(dialog.name.trim().toLowerCase()) &&
    dialog.name.trim().toLowerCase() !== ownNameLower;

  const saveMut = useMutation({
    mutationFn: async (d: typeof dialog) => {
      if (d.mode === "create") {
        return createSystem(
          powerPlantId,
          d.name.trim(),
          d.systemType,
          d.prefixes,
        );
      } else {
        return updateSystem(powerPlantId, d.id!, d.name.trim(), d.prefixes);
      }
    },
    onSuccess: (created, d) => {
      queryClient.invalidateQueries({ queryKey: ["systems", powerPlantId] });
      closeDialog();
      if (d.mode === "create" && created) onSelect(created.id);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteSystem(powerPlantId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["systems", powerPlantId] });
      if (selectedId === deleteId) onSelect(null);
      setDeleteId(null);
    },
  });

  const deleteName = systems.find((s) => s.id === deleteId)?.name;
  const menuSystem = systems.find((s) => s.id === menuAnchor?.id);
  const [exportSnackbarOpen, setExportSnackbarOpen] = useState(false);

  useEffect(() => {
    if (!menuAnchor) return;
    const handler = (e: MouseEvent) => {
      e.preventDefault();
      const menu = document.querySelector('[role="menu"]');
      if (menu?.contains(e.target as Node)) return;
      const elements = document.elementsFromPoint(e.clientX, e.clientY);
      const sysEl = elements.find(
        (el) => el instanceof HTMLElement && (el as HTMLElement).dataset.sysId,
      ) as HTMLElement | undefined;
      if (sysEl?.dataset.sysId) {
        setMenuAnchor({ id: sysEl.dataset.sysId, el: sysEl });
      } else {
        setMenuAnchor(null);
      }
    };
    document.addEventListener("contextmenu", handler, true);
    return () => document.removeEventListener("contextmenu", handler, true);
  }, [menuAnchor]);

  const handleExportSystem = async (systemId: string) => {
    try {
      await exportAssetMappingBySystem(powerPlantId, systemId);
      setExportSnackbarOpen(true);
    } catch (e) {
      reportBackendError(e instanceof Error ? e.message : "Export failed");
    }
  };

  const handleExportEvidencePack = async (systemId: string) => {
    try {
      await exportTotalEvidencePackBySystem(powerPlantId, systemId);
      setExportSnackbarOpen(true);
    } catch (e) {
      reportBackendError(e instanceof Error ? e.message : "Export failed");
    }
  };

  const handleExportRequiredEvidencePack = async (systemId: string) => {
    try {
      await exportRequiredEvidencePackBySystem(powerPlantId, systemId);
      setExportSnackbarOpen(true);
    } catch (e) {
      reportBackendError(e instanceof Error ? e.message : "Export failed");
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ color: "#002A5C", fontSize: "0.85rem", fontWeight: 700 }}
        >
          Systems
        </Typography>
        {isAdmin && (
          <Tooltip title="Add system" placement="right">
            <IconButton
              size="small"
              onClick={openCreate}
              sx={{
                color: "#8E99A8",
                "&:hover": {
                  color: "#009DE0",
                  bgcolor: "rgba(0,157,224,0.08)",
                },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Status messages */}
      {isLoading && (
        <Typography variant="caption" sx={{ px: 2, py: 1, color: "#999" }}>
          Loading…
        </Typography>
      )}
      {isError && (
        <Typography
          variant="caption"
          sx={{ px: 2, py: 1, color: "error.main" }}
        >
          Failed to load systems.
        </Typography>
      )}

      {/* List */}
      <List dense disablePadding>
        {systems.map((sys) => (
          <ListItemButton
            key={sys.id}
            data-sys-id={sys.id}
            selected={sys.id === selectedId}
            onClick={() => onSelect(sys.id)}
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setMenuAnchor({ id: sys.id, el: e.currentTarget });
            }}
            sx={{
              px: 2,
              py: 0.75,
              minHeight: 45,
              mx: 0.5,
              borderRadius: 1.5,
              mb: 0.25,
              color: sys.id === selectedId ? "#009DE0" : "#444",
              bgcolor:
                sys.id === selectedId ? "rgba(0,157,224,0.1)" : "transparent",
              "&:hover": {
                bgcolor:
                  sys.id === selectedId ? "rgba(0,157,224,0.15)" : "#EDF1F7",
                color: sys.id === selectedId ? "#009DE0" : "#002A5C",
              },
              "&.Mui-selected": { bgcolor: "rgba(0,157,224,0.1)" },
              "&.Mui-selected:hover": { bgcolor: "rgba(0,157,224,0.15)" },
            }}
          >
            <>
              <SettingsIcon
                sx={{ fontSize: 18, color: "#8E99A8", mr: 1, flexShrink: 0 }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "0.82rem",
                    fontWeight: sys.id === selectedId ? 600 : 400,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: sys.id === selectedId ? "#009DE0" : "inherit",
                  }}
                >
                  {sys.name}
                </Typography>
                {sys.system_type !== sys.name && (
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      color: "#8E99A8",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {sys.system_type}
                  </Typography>
                )}
              </Box>
              {sys.status && (
                <StatusChipFromSummary
                  status={sys.status}
                  percentage={sys.status_percentage}
                />
              )}
              {sys.lock_status && (
                <LockStatusIcon lockStatus={sys.lock_status} />
              )}
              <Box
                sx={{ ml: 0.5, flexShrink: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <IconButton
                  size="small"
                  onClick={(e) =>
                    setMenuAnchor({ id: sys.id, el: e.currentTarget })
                  }
                  sx={{
                    color: "#8E99A8",
                    p: 0.25,
                    "&:hover": { color: "#009DE0" },
                  }}
                >
                  <MoreVertIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </>
          </ListItemButton>
        ))}
      </List>

      <Menu
        anchorEl={menuAnchor?.el}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 220 } } }}
      >
        <MenuItem
          dense
          sx={{ fontSize: "0.82rem" }}
          onClick={() => {
            if (menuAnchor) handleExportSystem(menuAnchor.id);
            setMenuAnchor(null);
          }}
        >
          Export Asset Mapping
        </MenuItem>
        <ConditionalMenuItem
          disabled={menuSystem?.required_spof_lock_status !== "locked"}
          disabledTitle="All PIDs must be required-SPOF-locked first"
          onClick={() => {
            if (menuAnchor) handleExportRequiredEvidencePack(menuAnchor.id);
            setMenuAnchor(null);
          }}
        >
          Export Evidence Pack
        </ConditionalMenuItem>
        <ConditionalMenuItem
          disabled={menuSystem?.lock_status !== "locked"}
          disabledTitle="All PIDs must be locked first"
          onClick={() => {
            if (menuAnchor) handleExportEvidencePack(menuAnchor.id);
            setMenuAnchor(null);
          }}
        >
          Export Total Evidence Pack
        </ConditionalMenuItem>
        <ConditionalMenuItem
          disabled={!isAdmin}
          disabledTitle="Admin only"
          onClick={() => {
            if (menuAnchor && menuSystem) openEdit(menuSystem);
            setMenuAnchor(null);
          }}
        >
          Edit
        </ConditionalMenuItem>
        <ConditionalMenuItem
          disabled={!isAdmin}
          disabledTitle="Admin only"
          color="#E30613"
          onClick={() => {
            if (menuAnchor) setDeleteId(menuAnchor.id);
            setMenuAnchor(null);
          }}
        >
          Delete
        </ConditionalMenuItem>
      </Menu>
      <ConfirmDeleteDialog
        open={deleteId !== null}
        title="Delete System"
        message={
          <>
            Are you sure you want to delete <strong>{deleteName}</strong>? This
            will also delete all uploaded PIDs.
          </>
        }
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && deleteMut.mutate(deleteId)}
        isPending={deleteMut.isPending}
      />
      <Snackbar
        open={exportSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setExportSnackbarOpen(false)}
        message="Export requested — you will receive an email when it is ready."
      />

      {/* Create / Edit system dialog */}
      <Dialog open={dialog.open} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {dialog.mode === "create" ? "Add System" : "Edit System"}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              pt: 0.5,
            }}
          >
            <TextField
              autoFocus
              label="Display name"
              size="small"
              fullWidth
              value={dialog.name}
              onChange={(e) =>
                setDialog((d) => ({ ...d, name: e.target.value }))
              }
              error={isDuplicateName}
              helperText={isDuplicateName ? "Name already exists" : undefined}
            />
            <Autocomplete
              options={systemTypeOptions}
              value={dialog.systemType || null}
              onChange={(_e, v) =>
                setDialog((d) => ({ ...d, systemType: v ?? "" }))
              }
              disabled={dialog.mode === "edit"}
              size="small"
              renderInput={(params) => (
                <TextField {...params} label="System type" required />
              )}
            />
            <Autocomplete<string, true, false, true>
              multiple
              freeSolo
              options={[] as string[]}
              value={dialog.prefixes}
              inputValue={dialog.prefixesInput}
              onInputChange={(_e, v) =>
                setDialog((d) => ({ ...d, prefixesInput: v }))
              }
              onChange={(_e, v) =>
                setDialog((d) => ({ ...d, prefixes: v as string[] }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="KKS prefixes"
                  placeholder="Type a prefix and press Enter…"
                  helperText="Each prefix multiplies rows in the asset mapping export."
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              !dialog.name.trim() ||
              !dialog.systemType ||
              saveMut.isPending ||
              isDuplicateName
            }
            onClick={() => {
              const flushed = dialog.prefixesInput.trim()
                ? {
                    ...dialog,
                    prefixes: [...dialog.prefixes, dialog.prefixesInput.trim()],
                    prefixesInput: "",
                  }
                : dialog;
              saveMut.mutate(flushed);
            }}
          >
            {dialog.mode === "create" ? "Add" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
